import axios from 'axios';
import { useContext } from '@loadsmart/vue-feature-flags';
import { computed, shallowRef } from 'vue';
import { localStorageKeys } from '@/enums/local_storage';
import config from '@/config/config';
import NovaClient from '@/clients/nova/NovaClient';
import type { INovaUser } from '@/interfaces/nova';

type FeatureFlagContext = Parameters<ReturnType<typeof useContext>['updateContext']>[0] & {
  orgId?: string;
  role?: string;
  companyId?: string;
};

const me = shallowRef<INovaUser | null>(null);
const readonlyMe = computed(() => me.value);

const sessionClient = axios.create({
  baseURL: config.neutron_url,
  headers: NovaClient.getGlobalHeaders()
});

function getAccessToken() {
  return localStorage.getItem(localStorageKeys.ACCESS_TOKEN);
}

function setMe(user: INovaUser | null) {
  const { updateContext } = useContext();
  me.value = user;

  const context: FeatureFlagContext = {
    userId: user?.id,
    orgId: user?.orgId,
    role: user?.role,
    companyId: user?.companyId
  };
  return updateContext(context);
}

function setAccessToken(token: string) {
  sessionClient.defaults.headers.Authorization = `Bearer ${token}`;
}

function clearAccessToken() {
  localStorage.removeItem(localStorageKeys.ACCESS_TOKEN);
  delete sessionClient.defaults.headers.Authorization;
}

function login(token: string) {
  setAccessToken(token);
  localStorage.setItem(localStorageKeys.ACCESS_TOKEN, token);
}

function restoreAccessToken() {
  const token = getAccessToken();
  if (token) {
    setAccessToken(token);
  }
}

function logout() {
  setMe(null);
  clearAccessToken();
}

async function loadMe() {
  if (!getAccessToken()) {
    logout();
    return;
  }

  const response = await sessionClient.get('auth/me');

  if (response?.data) {
    setMe(response.data);
  } else {
    logout();
  }

  return response;
}

/** Session state is module-scoped on purpose: one signed-in user shared by every consumer. */
export function useSession() {
  return { me: readonlyMe, getAccessToken, login, restoreAccessToken, loadMe, logout };
}
